/**
 * `wayai pull` / `wayai push` against the `communities/` subtree (gh #5003): a community's name,
 * settings, rules, join link state, rooms and courses as files — `communities/<slug>/community.yaml`,
 * `rules.md` and `courses/` — synced three-way per entity (`model.ts`, the Sync rules table); the
 * courses' half is `courses.ts`.
 *
 * There is no preview: a community is one live store, so push edits what members see. Push
 * always prints what it will change and asks first (`-y` skips; with no terminal and no `-y` it
 * refuses), never creates a community and never deletes anything — a room, course, module or
 * lesson missing from the files is left as is; `archived: true` is how a room leaves, and
 * `published: false` how a course or a lesson does. Every request of a run, reads and writes,
 * goes through one `Pacer`. Each write's id and rev are written back to the files as soon as it
 * returns, so a push stopped anywhere leaves every rev true to the server and its rerun converges.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  COMMUNITY_ROOMS_MAX,
  type CommunityAdmission,
  type CommunityCourseCreateBody,
  type CommunityCourseUpdateBody,
  type CommunityJoinLink,
  type CommunityLessonCreateBody,
  type CommunityLessonUpdateBody,
  type CommunityModuleCreateBody,
  type CommunityModuleUpdateBody,
  type CommunityRoom,
  type CommunityRoomUpdateBody,
  type CommunitySettings,
  type CommunitySettingsBody,
  type CommunitySummary,
} from '@wayai/core/contracts';
import type { ApiClient } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { requireRealSubdirNoSymlink } from '../lib/fs-safety.js';
import { communitiesDirLabel, resolveLayout, WAYAI_LAYOUT } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { confirm } from '../lib/utils.js';
import { WORKSPACE_MANIFEST_LABEL } from '../lib/workspace-manifest.js';
import { assertInScope, seedScopeIfEmpty } from '../lib/worktree-scope.js';
import { COMMUNITY_FILE, resolveCommunityTarget } from '../lib/community-workspace.js';
import { isInteractive } from '../data/helpers.js';
import { HANDLED_SIGNALS, SIGNAL_EXIT_CODE, type HandledSignal } from '../lib/eval-session-control.js';
import { callCommunities, communitiesClient, communityNotFound, orgNotFound } from './api.js';
import { COMMUNITY_REQUESTS_PER_MINUTE, Pacer, paceDuration } from './pacer.js';
import { communityFilesSaver, readCommunityFolder, writeCommunityFolder, type CommunityFiles, type RoomEntry } from './files.js';
import { unlistedPath, writeCourseYaml, writeLessonFile } from './course-files.js';
import {
  CoursePush,
  ServerCourses,
  courseCapProblems,
  coursesChangedSince,
  coursesFromServer,
  planCourses,
  pullCourses,
  type CourseRemote,
  type CourseSaver,
  type CoursesPlan,
  type Write,
} from './courses.js';
import { emptyListing, lineDiff, printListing, show, type Listing } from './listing.js';
import {
  DEFAULT_ADMISSION,
  classify,
  createClientId,
  joinLinkRev,
  localName,
  nameContent,
  nameRev,
  roomContent,
  roomRev,
  serverRoom,
  serverSettings,
  settingsContent,
  settingsRev,
  type SettingsContent,
  type SyncState,
} from './model.js';

interface SyncArgs {
  autoConfirm: boolean;
  dryRun: boolean;
  theirs: boolean;
}

/** The flags this side reads. The target — the positional or `--community` — is the router's. */
function parseArgs(args: string[]): SyncArgs {
  return {
    autoConfirm: args.includes('--yes') || args.includes('-y'),
    dryRun: args.includes('--dry-run'),
    theirs: args.includes('--theirs'),
  };
}

function requireGitRoot(gitRoot: string | null): string {
  if (!gitRoot) throw expected('Not inside a git repository.');
  return gitRoot;
}

// ── The server side: one community, read and written through the pacer ──

interface ServerState {
  community: CommunitySummary;
  settings: CommunitySettings;
  rooms: CommunityRoom[];
  joinLink: CommunityJoinLink;
  courses: ServerCourses;
}

class Remote implements CourseRemote {
  constructor(
    private readonly api: ApiClient,
    private readonly pacer: Pacer,
    readonly communityId: string,
  ) {}

  private async send<T>(request: () => Promise<{ data: T }>, notFound = communityNotFound(this.communityId)): Promise<T> {
    return (await callCommunities(this.pacer.run(request), notFound)).data;
  }

  /**
   * gh #5003 Decision 8's bounded read: the org's list, settings, rooms, the join link and the
   * courses, then each course whole — one request per course. The org's list both binds the
   * community to the workspace's organization and names it.
   */
  async read(orgId: string): Promise<ServerState> {
    const communities = await this.send(() => this.api.listOrgCommunities(orgId), orgNotFound(orgId));
    const community = communities.find((c) => c.community_id === this.communityId);
    if (!community) {
      throw expected(
        `Community ${this.communityId} is not one of the communities of organization ${orgId}, the one ${WORKSPACE_MANIFEST_LABEL} names. ` +
          'A community is pulled and pushed from a workspace bound to the organization that owns it.',
      );
    }
    // The org's list first, so its refusal speaks before a bare 404; the rest do not depend on each other.
    const [settings, rooms, joinLink, courses] = await Promise.all([
      this.send(() => this.api.getCommunitySettings(this.communityId)),
      this.send(() => this.api.listCommunityRooms(this.communityId)),
      this.send(() => this.api.getCommunityJoinLink(this.communityId)),
      // Each course whole, as soon as the list names it.
      this.listCourses().then((list) => Promise.all(list.map((course) => this.exportCourse(course.course_id)))),
    ]);
    return { community, settings, rooms, joinLink, courses: new ServerCourses(courses) };
  }

  updateSettings(body: CommunitySettingsBody): Promise<CommunitySettings> {
    return this.send(() => this.api.updateCommunitySettings(this.communityId, body));
  }

  setJoinLink(enabled: boolean): Promise<CommunityJoinLink> {
    return this.send(() =>
      enabled ? this.api.replaceCommunityJoinLink(this.communityId) : this.api.disableCommunityJoinLink(this.communityId));
  }

  async createRoom(entry: RoomEntry): Promise<CommunityRoom> {
    const client_id = await createClientId(this.communityId, 'room', entry.key);
    return this.send(() => this.api.createCommunityRoom(this.communityId, { kind: entry.kind, name: entry.name, client_id }));
  }

  updateRoom(roomId: string, body: CommunityRoomUpdateBody): Promise<CommunityRoom> {
    return this.send(() => this.api.updateCommunityRoom(this.communityId, roomId, body));
  }

  createCourse(body: CommunityCourseCreateBody) {
    return this.send(() => this.api.createCommunityCourse(this.communityId, body));
  }

  updateCourse(courseId: string, body: CommunityCourseUpdateBody) {
    return this.send(() => this.api.updateCommunityCourse(this.communityId, courseId, body));
  }

  createModule(courseId: string, body: CommunityModuleCreateBody) {
    return this.send(() => this.api.createCommunityModule(this.communityId, courseId, body));
  }

  updateModule(courseId: string, moduleId: string, body: CommunityModuleUpdateBody) {
    return this.send(() => this.api.updateCommunityModule(this.communityId, courseId, moduleId, body));
  }

  createLesson(courseId: string, body: CommunityLessonCreateBody) {
    return this.send(() => this.api.createCommunityLesson(this.communityId, courseId, body));
  }

  updateLesson(courseId: string, lessonId: string, body: CommunityLessonUpdateBody) {
    return this.send(() => this.api.updateCommunityLesson(this.communityId, courseId, lessonId, body));
  }

  listCourses() {
    return this.send(() => this.api.listCommunityCourseDrafts(this.communityId));
  }

  exportCourse(courseId: string) {
    return this.send(() => this.api.exportCommunityCourse(this.communityId, courseId));
  }
}

async function openRemote(communityId: string): Promise<Remote> {
  const pacer = new Pacer({
    onWait: (ms, reason) =>
      console.error(
        reason === 'paced'
          ? `  Pacing at ${COMMUNITY_REQUESTS_PER_MINUTE} requests a minute: waiting ${Math.ceil(ms / 1000)} s.`
          : `  The community request budget is spent (your web console and messages share it): waiting ${Math.ceil(ms / 1000)} s.`,
      ),
  });
  return new Remote(await communitiesClient(), pacer, communityId);
}

// ── The plan: every entity's verdict ──

interface SettingsPlan {
  state: SyncState;
  file: SettingsContent;
  server: SettingsContent;
  serverRev: string;
}

interface NamePlan {
  state: SyncState;
  file: string;
  server: string;
  serverRev: string;
}

interface JoinLinkPlan {
  state: SyncState;
  file: boolean;
  server: boolean;
  serverRev: string;
}

type RoomPlan =
  | { kind: 'create'; entry: RoomEntry }
  | { kind: 'existing'; entry: RoomEntry; server: CommunityRoom; state: SyncState; serverRev: string };

interface Plan {
  name: NamePlan;
  settings: SettingsPlan;
  joinLink: JoinLinkPlan;
  rooms: RoomPlan[];
  /** On the server, not in the files: left as is by push, added to the files by pull. */
  untracked: CommunityRoom[];
  /** In the files with an id the server does not have: refused by both verbs. */
  unknownIds: RoomEntry[];
  courses: CoursesPlan;
}

function fileSettingsContent(files: CommunityFiles): SettingsContent {
  return settingsContent({ rules: files.rules, ...files.settings });
}

function planSync(files: CommunityFiles, server: ServerState): Plan {
  const id = files.community_id;
  const fileSettings = fileSettingsContent(files);
  const settingsSide = serverSettings(id, server.settings);
  const fileName = nameContent(files.name);
  const serverName = nameContent(server.community.name);
  const nameServerRev = nameRev(id, serverName);
  const nameFileRev = nameRev(id, fileName);
  // Files written before `name_rev` held the name as last synced (push refused a rename, pull took
  // the server's), so their own name is the base: a web rename since reads as one. Agreeing, they
  // converge, which records the rev.
  const nameState = files.name_rev === undefined && nameFileRev === nameServerRev
    ? 'converged'
    : classify(files.name_rev ?? nameFileRev, nameFileRev, nameServerRev);
  const joinLinkServerRev = joinLinkRev(id, { enabled: server.joinLink.enabled });
  const byId = new Map(server.rooms.map((room) => [room.room_id, room]));
  const rooms: RoomPlan[] = [];
  const unknownIds: RoomEntry[] = [];
  for (const entry of files.rooms) {
    if (entry.id === undefined) {
      rooms.push({ kind: 'create', entry });
      continue;
    }
    const room = byId.get(entry.id);
    if (!room) {
      unknownIds.push(entry);
      continue;
    }
    const roomSide = serverRoom(room);
    const state = classify(entry.rev, roomRev(room.room_id, roomContent(entry)), roomSide.rev);
    rooms.push({ kind: 'existing', entry, server: room, state, serverRev: roomSide.rev });
  }
  const tracked = new Set(files.rooms.map((room) => room.id));
  return {
    name: {
      state: nameState,
      file: fileName.name,
      server: serverName.name,
      serverRev: nameServerRev,
    },
    settings: {
      state: classify(files.settings.rev, settingsRev(id, fileSettings), settingsSide.rev),
      file: fileSettings,
      server: settingsSide.content,
      serverRev: settingsSide.rev,
    },
    joinLink: {
      state: classify(files.join_link_rev, joinLinkRev(id, { enabled: files.join_link_enabled }), joinLinkServerRev),
      file: files.join_link_enabled,
      server: server.joinLink.enabled,
      serverRev: joinLinkServerRev,
    },
    rooms,
    untracked: server.rooms.filter((room) => !tracked.has(room.room_id)),
    unknownIds,
    courses: planCourses(files, server.courses),
  };
}

/** Ids the server does not have where the files put them: refused by both verbs. */
function unknownIdProblems(plan: Plan): string[] {
  return [...plan.unknownIds.map((entry) => `room ${entry.key}: ${entry.id} is not a room of this community`), ...plan.courses.problems];
}

/** The folders and lesson files no list names: drafts in progress, skipped by both verbs. */
function warnUnlisted(files: CommunityFiles): void {
  for (const folder of files.unlistedCourses) {
    console.warn(`Skipped: ${unlistedPath(folder.name)} — ${COMMUNITY_FILE}'s courses does not list it.`);
  }
  for (const course of files.courses) {
    for (const file of course.unlisted) {
      console.warn(`Skipped: ${unlistedPath(file.name, course)} — no module of the course lists it.`);
    }
  }
}

// ── Rendering ──

function settingsChanges(from: SettingsContent, to: SettingsContent): string[] {
  const out: string[] = [];
  if (from.slow_mode_seconds !== to.slow_mode_seconds) out.push(`slow_mode_seconds ${from.slow_mode_seconds} → ${to.slow_mode_seconds}`);
  if (from.courses_label !== to.courses_label) out.push(`courses_label ${show(from.courses_label)} → ${show(to.courses_label)}`);
  if (from.admission !== to.admission) out.push(`admission ${from.admission} → ${to.admission}`);
  if (from.rules !== to.rules) out.push('rules (rules.md)');
  return out;
}

/** What a push's change of the admission does to who may join, said before it is applied (gh #5042 Decision 4). */
function admissionChange(to: CommunityAdmission): string {
  return to === 'invite'
    ? 'From now on the link admits only the organization\'s owners and admins and the emails on the access list ' +
        '(wayai communities access). Current members stay, including anyone who joined with a shared link; ' +
        'any other member who leaves needs their email on the list to join again.'
    : 'From now on anyone with the join link can join. The access list is kept.';
}

function roomChanges(from: { name: string; archived: boolean }, to: { name: string; archived: boolean }): string[] {
  const out: string[] = [];
  if (from.name !== to.name) out.push(`name ${show(from.name)} → ${show(to.name)}`);
  if (from.archived !== to.archived) out.push(to.archived ? 'archived' : 'brought back');
  return out;
}

const onOff = (enabled: boolean) => (enabled ? 'on' : 'off');

function joinLinkChange(from: boolean, to: boolean): string {
  return to
    ? `${onOff(from)} → on: a new link — every link shared before stays dead`
    : `${onOff(from)} → off: every link shared before stops working`;
}

// ── push ──

/**
 * One phase of a push's writes: known from the plan, or — the places and the publishing, which
 * depend on what the creates before them answered — computed again when the phase is reached,
 * the plan's prediction (`planned`) counted before the question.
 */
type Phase = Write[] | { planned: number; later: () => Write[] };

/** A phase's writes counted before the question; a computed phase's are one request each. */
const plannedCount = (phase: Phase): number => (Array.isArray(phase) ? phase.length : phase.planned);
const plannedRequests = (phase: Phase): number => (Array.isArray(phase) ? phase.reduce((n, write) => n + write.requests, 0) : phase.planned);

/** Refusals from the read, before any write: unknown ids, read-only fields, the caps. */
function pushProblems(files: CommunityFiles, server: ServerState, plan: Plan): string[] {
  const problems = unknownIdProblems(plan);
  for (const room of plan.rooms) {
    if (room.kind === 'existing' && room.entry.kind !== room.server.kind) {
      problems.push(`room ${room.entry.key}: kind cannot change once a room exists (it is ${room.server.kind})`);
    }
  }
  // A create may be the retry of one whose answer was lost, which the server answers with the room
  // it made — one of the rooms the files do not list. Only the creates no such room can explain
  // are certainly new.
  const creates = plan.rooms.filter((room) => room.kind === 'create').length;
  const certainlyNew = Math.max(0, creates - plan.untracked.length);
  if (server.rooms.length + certainlyNew > COMMUNITY_ROOMS_MAX) {
    problems.push(
      `rooms: a community holds at most ${COMMUNITY_ROOMS_MAX} rooms, archived ones included; it has ${server.rooms.length}, and the files create ${creates}`,
    );
  }
  problems.push(...courseCapProblems(files, plan.courses));
  return problems;
}

function settingsPatch(files: CommunityFiles, server: SettingsContent): CommunitySettingsBody {
  const file = fileSettingsContent(files);
  return {
    // Raw: the text as the file holds it; the server trims it and reads '' as none.
    ...(file.rules !== server.rules ? { rules: files.rules } : {}),
    ...(file.slow_mode_seconds !== server.slow_mode_seconds ? { slow_mode_seconds: files.settings.slow_mode_seconds } : {}),
    ...(file.courses_label !== server.courses_label ? { courses_label: files.settings.courses_label } : {}),
    ...(file.admission !== server.admission ? { admission: file.admission } : {}),
  };
}

function roomPatch(entry: RoomEntry, room: CommunityRoom): CommunityRoomUpdateBody | null {
  const file = roomContent(entry);
  const server = roomContent(room);
  const body: CommunityRoomUpdateBody = {
    ...(file.name !== server.name ? { name: entry.name } : {}),
    ...(file.archived !== server.archived ? { archived: entry.archived } : {}),
  };
  return Object.keys(body).length > 0 ? body : null;
}

/** Record a server room's answer in its entry: content, id and rev. */
function takeRoom(entry: RoomEntry, room: CommunityRoom): void {
  entry.id = room.room_id;
  entry.rev = serverRoom(room).rev;
  entry.name = room.name;
  entry.archived = room.archived;
  entry.kind = room.kind;
}

function takeName(files: CommunityFiles, name: string): void {
  files.name = name;
  files.name_rev = nameRev(files.community_id, nameContent(name));
}

function takeSettings(files: CommunityFiles, settings: CommunitySettings): void {
  const { content, rev } = serverSettings(files.community_id, settings);
  files.settings = {
    rev,
    slow_mode_seconds: settings.slow_mode_seconds,
    courses_label: settings.courses_label ?? '',
    admission: content.admission,
  };
  files.rules = settings.rules ?? '';
}

function takeJoinLink(files: CommunityFiles, enabled: boolean): void {
  files.join_link_enabled = enabled;
  files.join_link_rev = joinLinkRev(files.community_id, { enabled });
}

/**
 * The writes a push makes, phase by phase in the apply order — unpublish → name → settings → join link →
 * rooms → courses → modules → lessons → places → publish — each writing its id and rev back once
 * it returns.
 */
function pushPhases(files: CommunityFiles, plan: Plan, remote: Remote, save: () => void, courses: CoursePush): Phase[] {
  return [
    courses.unpublishWrites(),
    communityWrites(files, plan, remote, save),
    courses.courseWrites(),
    courses.moduleWrites(),
    courses.lessonWrites(),
    { planned: courses.plannedPlaces, later: () => courses.placeWrites() },
    { planned: courses.publishWrites().length, later: () => courses.publishWrites() },
  ];
}

/** The name, settings, the join link and rooms. */
function communityWrites(files: CommunityFiles, plan: Plan, remote: Remote, save: () => void): Write[] {
  const writes: Write[] = [];
  if (plan.name.state === 'local') {
    const name = plan.name.file;
    writes.push({
      label: 'name',
      requests: 1,
      apply: async () => {
        const answered = (await remote.updateSettings({ name })).name;
        if (answered === undefined) throw expected('the server answered no name: it may be mid-deploy, push again in a moment');
        takeName(files, answered);
        save();
      },
    });
  }
  if (plan.settings.state === 'local') {
    const body = settingsPatch(files, plan.settings.server);
    writes.push({
      label: 'settings',
      requests: 1,
      apply: async () => {
        takeSettings(files, await remote.updateSettings(body));
        save();
      },
    });
  }
  if (plan.joinLink.state === 'local') {
    const enabled = plan.joinLink.file;
    writes.push({
      label: `join link ${onOff(enabled)}`,
      requests: 1,
      apply: async () => {
        takeJoinLink(files, (await remote.setJoinLink(enabled)).enabled);
        save();
      },
    });
  }
  for (const room of plan.rooms) {
    if (room.kind !== 'create') continue;
    const { entry } = room;
    writes.push({
      label: `room ${entry.key} (create)`,
      requests: entry.archived ? 2 : 1,
      apply: async () => {
        const made = await remote.createRoom(entry);
        // A `client_id` already used answers the room made with it, as it stands (Decision 9).
        if (made.kind !== entry.kind) {
          throw expected(
            `the key ${entry.key} already names room ${show(made.name)} (${made.room_id}, a ${made.kind} room) in this community: ` +
              'a key keeps naming its room. Give the new room another key, or set its kind to that room\'s.',
          );
        }
        const twin = files.rooms.find((other) => other !== entry && other.id === made.room_id);
        if (twin) {
          throw expected(
            `the key ${entry.key} already names room ${made.room_id}, which your files also list as ${twin.key}: remove one of the two entries.`,
          );
        }
        // The id and the server's rev are recorded at once, with the files' own name and archived
        // state kept: a push stopped before the reconcile below then reads the room as changed in
        // the files, and its rerun sends what this one did not.
        const wanted = { name: entry.name, archived: entry.archived };
        takeRoom(entry, made);
        Object.assign(entry, wanted);
        save();
        // Reconcile: the answer may be a room made by an earlier attempt, since renamed or archived,
        // and a new room is never created archived.
        const patch = roomPatch(entry, made);
        if (patch) {
          takeRoom(entry, await remote.updateRoom(made.room_id, patch));
          save();
        }
      },
    });
  }
  for (const room of plan.rooms) {
    if (room.kind !== 'existing' || room.state !== 'local') continue;
    const { entry, server } = room;
    const patch = roomPatch(entry, server);
    if (!patch) continue;
    writes.push({
      label: `room ${entry.key}`,
      requests: 1,
      apply: async () => {
        takeRoom(entry, await remote.updateRoom(server.room_id, patch));
        save();
      },
    });
  }
  return writes;
}

/** Revs to refresh without a write: both sides agree, or the files carried no base. */
function refreshConverged(files: CommunityFiles, plan: Plan): number {
  let refreshed = 0;
  if (plan.name.state === 'converged') {
    files.name_rev = plan.name.serverRev;
    refreshed++;
  }
  if (plan.settings.state === 'converged') {
    files.settings.rev = plan.settings.serverRev;
    refreshed++;
  }
  if (plan.joinLink.state === 'converged') {
    files.join_link_rev = plan.joinLink.serverRev;
    refreshed++;
  }
  for (const room of plan.rooms) {
    if (room.kind === 'existing' && room.state === 'converged') {
      room.entry.rev = room.serverRev;
      room.entry.kind = room.server.kind;
      refreshed++;
    }
  }
  return refreshed;
}

function pushListing(plan: Plan, courses: CoursePush): Listing {
  const listing = emptyListing();
  const { name, settings, joinLink } = plan;
  if (name.state === 'local') listing.changes.push(`~ name: ${show(name.server)} → ${show(name.file)}`);
  if (settings.state === 'local') {
    listing.changes.push(`~ settings: ${settingsChanges(settings.server, settings.file).join('; ')}`);
    if (settings.server.admission !== settings.file.admission) listing.changes.push(`    ${admissionChange(settings.file.admission)}`);
    if (settings.server.rules !== settings.file.rules) {
      listing.changes.push(...lineDiff(settings.server.rules, settings.file.rules).map((line) => `    ${line}`));
    }
  }
  if (joinLink.state === 'local') listing.changes.push(`~ join link: ${joinLinkChange(joinLink.server, joinLink.file)}`);
  for (const room of plan.rooms) {
    const { entry } = room;
    if (room.kind === 'create') {
      listing.changes.push(`+ room ${entry.key}: create ${show(entry.name)} (${entry.kind})${entry.archived ? ', archived' : ''}`);
    } else if (room.state === 'local') {
      listing.changes.push(`~ room ${entry.key}: ${roomChanges(roomContent(room.server), roomContent(entry)).join('; ')}`);
    }
  }
  const named = (state: SyncState) => [
    ...(name.state === state ? ['name'] : []),
    ...(settings.state === state ? ['settings'] : []),
    ...(joinLink.state === state ? ['join link'] : []),
    ...plan.rooms.flatMap((room) => (room.kind === 'existing' && room.state === state ? [`room ${room.entry.key}`] : [])),
  ];
  listing.serverChanged = named('remote');
  listing.conflicts = named('conflict');
  listing.leftAsIs = plan.untracked.map((room) => `room ${show(room.name)} (${room.room_id})`);
  courses.list(listing);
  return listing;
}

export async function pushCommunity(gitRootOrNull: string | null, selector: string | undefined, args: string[]): Promise<void> {
  const { autoConfirm, dryRun, theirs } = parseArgs(args);
  if (theirs) throw expected('`--theirs` is a pull option: `wayai pull communities/<folder> --theirs` takes the server\'s side of a conflict.');
  const gitRoot = requireGitRoot(gitRootOrNull);
  const target = resolveCommunityTarget(gitRoot, selector);
  if (target.folder === null) {
    throw expected(
      `No folder in ${communitiesDirLabel(gitRoot)}/ is community ${target.communityId}. Pull it first: wayai pull ${WAYAI_LAYOUT.communitiesSubdir}/${target.communityId}`,
    );
  }
  const { folder } = target;
  const files = readCommunityFolder(folder);
  if (!dryRun && !autoConfirm && !isInteractive()) {
    throw expected('Push changes the live community and asks first: pass -y when not running in a terminal, or --dry-run to only list the changes.');
  }
  const { organization_id: orgId } = requireRepoConfig(gitRoot);
  const label = path.basename(folder);
  assertInScope('communities', files.community_id, label);

  warnUnlisted(files);
  const remote = await openRemote(files.community_id);
  const server = await remote.read(orgId);
  const plan = planSync(files, server);
  console.log(`Community ${show(server.community.name)} (${server.community.community_id}) — ${path.relative(gitRoot, folder)}/`);

  const problems = pushProblems(files, server, plan);
  if (problems.length > 0) {
    throw expected(`Nothing was pushed:\n${problems.map((p) => `  - ${p}`).join('\n')}`);
  }
  const { communitiesDir } = resolveLayout(gitRoot);
  const save = communityFilesSaver(communitiesDir, folder, files);
  const courses = new CoursePush(files, plan.courses, remote, courseSaver(communitiesDir, folder, save));
  const listing = pushListing(plan, courses);
  printListing(listing);
  if (listing.conflicts.length > 0) {
    throw expected(`\nNothing was pushed: ${listing.conflicts.length} conflict(s). ${resolveConflicts(folder)}`);
  }

  const phases = pushPhases(files, plan, remote, save, courses);
  const requests = phases.reduce((sum, phase) => sum + plannedRequests(phase), 0);
  if (requests === 0) {
    const refreshed = dryRun ? 0 : refreshConverged(files, plan) + courses.refreshConverged();
    if (refreshed > 0) save();
    console.log(`\nNo changes to push${refreshed > 0 ? ` (${refreshed} rev(s) refreshed)` : ''}.`);
    if (!dryRun) seedScopeIfEmpty('communities', files.community_id);
    return;
  }

  console.log(
    `\nThis push makes ${requests} request(s) to the live community — ${paceDuration(requests)} at ${COMMUNITY_REQUESTS_PER_MINUTE} a minute.`,
  );
  if (dryRun) {
    console.log('Dry run — nothing was written.');
    return;
  }
  if (!autoConfirm) {
    if (!(await confirm('Apply these changes to the live community?'))) {
      console.log('Cancelled. Nothing was written.');
      return;
    }
    // The question can wait for minutes, and a web edit made meanwhile would be written over
    // with nothing left to show it. Read again: a change since the listing stops the push.
    const changed = changedSince(server, await remote.read(orgId));
    if (changed.length > 0) {
      throw expected(
        `The community changed while the question waited (${changed.join(', ')}). Nothing was written: push again to see the changes.`,
      );
    }
  }

  refreshConverged(files, plan);
  courses.refreshConverged();
  save();
  let total = phases.reduce((sum, phase) => sum + plannedCount(phase), 0);
  let done = 0;
  const stop = stopAfterWrite();
  try {
    for (const phase of phases) {
      const writes = Array.isArray(phase) ? phase : phase.later();
      total += writes.length - plannedCount(phase);
      for (const write of writes) {
        if (stop.requested()) {
          throw expected(
            `Push stopped on request after ${done} change(s). What it applied is on the community, and the files record it: push again to go on.`,
          );
        }
        try {
          await write.apply();
        } catch (err) {
          throw expected(
            `Push stopped at ${write.label}: ${stopReason(err)}\n` +
              'What it applied before stopping is on the community, and the files record it. Fix the cause and push again.',
          );
        }
        console.log(`  [${++done}/${total}] ${write.label}`);
      }
    }
  } finally {
    stop.release();
  }
  console.log(
    `\nPushed ${done} change(s). Commit ${path.relative(gitRoot, folder)}/: the files now record the ids and revs this push wrote.`,
  );
  seedScopeIfEmpty('communities', files.community_id);
}

/**
 * Ctrl-C (or a closed terminal) while push writes: the write in flight finishes and is recorded,
 * then the push stops, so the files stay true to the server. A second one leaves at once.
 */
function stopAfterWrite(): { requested: () => boolean; release: () => void } {
  let asked = false;
  const onSignal = (signal: HandledSignal) => {
    if (asked) process.exit(SIGNAL_EXIT_CODE[signal]);
    asked = true;
    console.error('\n  Stopping after the write in progress, once the files record it. Press Ctrl-C again to leave at once.');
  };
  for (const signal of HANDLED_SIGNALS) process.on(signal, onSignal);
  return {
    requested: () => asked,
    release: () => {
      for (const signal of HANDLED_SIGNALS) process.off(signal, onSignal);
    },
  };
}

/** Where a push records each course write: the files it changed, no-follow. */
function courseSaver(root: string, folder: string, community: () => void): CourseSaver {
  return {
    community,
    course: (course) => {
      writeCourseYaml(root, folder, course);
    },
    lesson: (course, lesson) => {
      writeLessonFile(root, folder, course, lesson);
    },
  };
}

/** The entities whose server state differs between two reads of one community. */
function changedSince(before: ServerState, after: ServerState): string[] {
  const id = before.community.community_id;
  const changed: string[] = [];
  if (before.community.name !== after.community.name) changed.push('name');
  if (serverSettings(id, before.settings).rev !== serverSettings(id, after.settings).rev) changed.push('settings');
  // The link too, not only its state: a link rotated meanwhile may already be shared.
  if (before.joinLink.enabled !== after.joinLink.enabled || before.joinLink.url !== after.joinLink.url) {
    changed.push('join link');
  }
  const revs = new Map(before.rooms.map((room) => [room.room_id, serverRoom(room).rev]));
  for (const room of after.rooms) {
    if (revs.get(room.room_id) !== serverRoom(room).rev) changed.push(`room ${show(room.name)}`);
  }
  changed.push(...coursesChangedSince(before.courses, after.courses));
  return changed;
}

function stopReason(err: unknown): string {
  return sanitizeTerminalText(extractApiMessage(err));
}

// ── pull ──

/** Add the server's rooms the files do not list, each under a new stable key. */
function addUntracked(files: CommunityFiles, rooms: CommunityRoom[]): string[] {
  const taken = new Set(files.rooms.map((room) => room.key));
  return rooms.map((room) => {
    const key = localName(room.name, room.room_id, taken, 'room');
    taken.add(key);
    const entry: RoomEntry = { key, name: room.name, kind: room.kind, archived: room.archived };
    takeRoom(entry, room);
    files.rooms.push(entry);
    return `+ room ${key}: ${show(room.name)} (${room.kind})${room.archived ? ', archived' : ''}`;
  });
}

function filesFromServer(server: ServerState): CommunityFiles {
  const files: CommunityFiles = {
    community_id: server.community.community_id,
    name: server.community.name,
    settings: { slow_mode_seconds: 0, courses_label: '', admission: DEFAULT_ADMISSION },
    rules: '',
    join_link_enabled: false,
    rooms: [],
    courses: [],
    unlistedCourses: [],
  };
  takeName(files, server.community.name);
  takeSettings(files, server.settings);
  takeJoinLink(files, server.joinLink.enabled);
  addUntracked(files, server.rooms);
  coursesFromServer(files, server.courses);
  return files;
}

/** Apply the pull column of the Sync rules table to the files; returns what it changed and left. */
function pullMerge(files: CommunityFiles, server: ServerState, plan: Plan, theirs: boolean): Listing {
  const listing = emptyListing();
  const takes = (state: SyncState) => state === 'remote' || (state === 'conflict' && theirs);
  const note = (state: SyncState, what: string) => {
    if (state === 'local') listing.kept.push(what);
    if (state === 'conflict' && !theirs) listing.conflicts.push(what);
  };

  const { name, settings, joinLink } = plan;
  if (takes(name.state)) {
    listing.changes.push(`~ name: ${show(files.name)} → ${show(name.server)}`);
    takeName(files, server.community.name);
  }
  note(name.state, 'name');
  if (takes(settings.state)) {
    listing.changes.push(`~ settings: ${settingsChanges(settings.file, settings.server).join('; ')}`);
    takeSettings(files, server.settings);
  }
  note(settings.state, 'settings');
  if (takes(joinLink.state)) {
    listing.changes.push(`~ join link: ${onOff(joinLink.file)} → ${onOff(joinLink.server)}`);
    takeJoinLink(files, server.joinLink.enabled);
  }
  note(joinLink.state, 'join link');
  for (const room of plan.rooms) {
    if (room.kind === 'create') {
      listing.kept.push(`room ${room.entry.key} (to create)`);
      continue;
    }
    const { entry, server: serverSide, state } = room;
    if (takes(state)) {
      listing.changes.push(`~ room ${entry.key}: ${roomChanges(roomContent(entry), roomContent(serverSide)).join('; ')}`);
      takeRoom(entry, serverSide);
    } else if (entry.kind !== serverSide.kind) {
      listing.changes.push(`~ room ${entry.key}: kind ${entry.kind} → ${serverSide.kind} (it never changes)`);
      entry.kind = serverSide.kind;
    }
    note(state, `room ${entry.key}`);
  }
  refreshConverged(files, plan);
  listing.changes.push(...addUntracked(files, plan.untracked));
  // After the rooms: a lesson names its room by the key a new room gets here.
  pullCourses(files, plan.courses, takes, note, listing);
  return listing;
}

export async function pullCommunity(gitRootOrNull: string | null, selector: string | undefined, args: string[]): Promise<void> {
  const { dryRun, theirs } = parseArgs(args);
  const gitRoot = requireGitRoot(gitRootOrNull);
  const target = resolveCommunityTarget(gitRoot, selector);
  // An existing folder's files are read and checked before any request; a first pull has none.
  let existing: { folder: string; files: CommunityFiles } | null = null;
  let communityId: string;
  if (target.folder === null) {
    communityId = target.communityId;
  } else {
    existing = { folder: target.folder, files: readCommunityFolder(target.folder) };
    communityId = existing.files.community_id;
  }
  const { organization_id: orgId } = requireRepoConfig(gitRoot);
  assertInScope('communities', communityId, existing ? path.basename(existing.folder) : undefined);

  const { communitiesDir } = resolveLayout(gitRoot);
  const remote = await openRemote(communityId);
  const server = await remote.read(orgId);

  if (!existing) {
    // Compared case-insensitively: on a case-insensitive filesystem `ACME/` is `acme/`.
    const taken = new Set(safeReaddir(communitiesDir).map((name) => name.toLowerCase()));
    const folder = path.join(communitiesDir, localName(server.community.name, communityId, taken, 'community'));
    const files = filesFromServer(server);
    const where = `${path.relative(gitRoot, folder)}/`;
    const counts = `${files.rooms.length} room(s), ${files.courses.length} course(s)`;
    if (dryRun) {
      console.log(`Dry run — would write community ${show(server.community.name)} to ${where} with ${counts}.`);
      return;
    }
    requireRealSubdirNoSymlink(gitRoot, communitiesDir, true);
    writeCommunityFolder(communitiesDir, folder, files);
    console.log(`Community ${show(server.community.name)} written to ${where} (${counts}). Commit it: its ids and revs are the base the next push compares against.`);
    seedScopeIfEmpty('communities', communityId);
    return;
  }

  const { folder, files: local } = existing;
  warnUnlisted(local);
  const plan = planSync(local, server);
  const problems = unknownIdProblems(plan);
  if (problems.length > 0) {
    throw expected(`Nothing was pulled:\n${problems.map((p) => `  - ${p}`).join('\n')}`);
  }
  const listing = pullMerge(local, server, plan, theirs);
  console.log(`Community ${show(server.community.name)} (${communityId}) — ${path.relative(gitRoot, folder)}/`);
  printListing(listing);
  if (dryRun) {
    console.log('\nDry run — nothing was written.');
  } else {
    const written = writeCommunityFolder(communitiesDir, folder, local);
    console.log(written.length > 0 ? `\nWrote ${written.join(', ')}.` : '\nAlready up to date.');
    seedScopeIfEmpty('communities', communityId);
  }
  if (listing.conflicts.length > 0) {
    throw expected(`${listing.conflicts.length} conflict(s) left as they are in your files. ${resolveConflicts(folder)}`);
  }
}

/** How a conflict is resolved: the server's side is taken, and an edit to keep is made again on top of it. */
function resolveConflicts(folder: string): string {
  return (
    `To resolve, take the server's side — wayai pull ${WAYAI_LAYOUT.communitiesSubdir}/${path.basename(folder)} --theirs — ` +
    'then make again any edit of yours to keep, and push.'
  );
}

function safeReaddir(dir: string): string[] {
  try {
    return fs.readdirSync(dir);
  } catch {
    return [];
  }
}
